import { useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { getElementScore, getModalityScore, getCategoryScores, COMPATIBILITY_CATEGORIES } from '../lib/compatibility'
import { generateCompatibilityDescription } from '../lib/compatibilityDescriptions'
import { getZodiacSign } from '../utils/zodiac'
import './CompatibilityModal.css'

const CompatibilityModal = ({ profile, currentProfile, onClose, fullContent = true }) => {
  const { t } = useTranslation()

  const { description, categoryScores } = useMemo(() => {
    if (!profile || !currentProfile || !currentProfile.date_of_birth) {
      return { description: null, categoryScores: null }
    }

    const currentBirthDate = new Date(currentProfile.date_of_birth)
    const currentZodiac = getZodiacSign(
      currentBirthDate.getDate(),
      currentBirthDate.getMonth() + 1
    )

    const profileBirthDate = profile.date_of_birth || profile.dateOfBirth
    if (!profileBirthDate) {
      return { description: null, categoryScores: null }
    }

    const profileBirth = new Date(profileBirthDate)
    const profileZodiac = getZodiacSign(
      profileBirth.getDate(),
      profileBirth.getMonth() + 1
    )

    if (!currentZodiac.name || !profileZodiac.name) {
      return { description: null, categoryScores: null }
    }

    const elementScore = getElementScore(currentZodiac.name, profileZodiac.name)
    const modalityScore = getModalityScore(currentZodiac.name, profileZodiac.name)

    const description = generateCompatibilityDescription(
      elementScore,
      modalityScore,
      currentProfile.id,
      profile.id
    )

    const categoryScores = getCategoryScores(
      currentZodiac.name,
      profileZodiac.name,
      currentProfile.id,
      profile.id
    )

    return { description, categoryScores }
  }, [profile, currentProfile])

  if (!profile) return null

  const modal = (
    <div className="compatibility-modal-overlay" onClick={onClose}>
      <div className="compatibility-modal" onClick={(e) => e.stopPropagation()}>
        <button className="compatibility-modal-close" onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>

        <h2 className="compatibility-modal-title">{t('compatibility.title')}</h2>

        <div className="compatibility-content">
          {fullContent && categoryScores && (
            <div className="compatibility-scores-grid">
              {COMPATIBILITY_CATEGORIES.map((key) => (
                <div key={key} className="compatibility-score-item">
                  <div
                    className="compatibility-circle-ring"
                    style={{ '--p': categoryScores[key] }}
                    role="img"
                    aria-label={`${t('compatibility.categories.' + key)}: ${categoryScores[key]}%`}
                  >
                    <span className="compatibility-circle-value">{categoryScores[key]}%</span>
                  </div>
                  <span className="compatibility-circle-label">{t('compatibility.categories.' + key)}</span>
                </div>
              ))}
            </div>
          )}

          {fullContent && description ? (
            <>
              {description.positives.map((text, index) => (
                <p key={index} className="compatibility-positive">
                  {text}
                </p>
              ))}
              {description.keepInMind.length > 0 && (
                <div className="compatibility-keep-in-mind">
                  <h3 className="keep-in-mind-title">{t('compatibility.keepInMind')}</h3>
                  {description.keepInMind.map((text, index) => (
                    <p key={index} className="keep-in-mind-text">
                      {text}
                    </p>
                  ))}
                </div>
              )}
            </>
          ) : !fullContent ? (
            <p className="compatibility-draft">{t('compatibility.fullInsightsAvailable')}</p>
          ) : null}
        </div>
      </div>
    </div>
  )

  return createPortal(modal, document.body)
}

export default CompatibilityModal
